/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/** Default ON — set VITE_ABR_REBRAND=false to restore Coze branding. */
export function isAbrRebrandEnabled(): boolean {
  const raw = import.meta.env.VITE_ABR_REBRAND;
  if (raw === undefined || raw === '') {
    return true;
  }
  return raw !== 'false' && raw !== '0';
}

/** 新版默认工作空间入口 */
export const ABR_DEFAULT_ENTRY_PATH = '/space';
/** @deprecated 使用 ABR_DEFAULT_ENTRY_PATH */
export const ABR_V2_ENTRY_PATH = ABR_DEFAULT_ENTRY_PATH;

/** 旧版 v1 工作空间入口 */
export const COZE_V1_ENTRY_PATH = '/v1/space';
/** @deprecated 使用 COZE_V1_ENTRY_PATH */
export const COZE_DEFAULT_ENTRY_PATH = COZE_V1_ENTRY_PATH;

export const COZE_V1_ROUTE_PREFIX = '/v1';

/** 新版首页路由段 */
export const ABR_HOME_SUBMENU = 'develop';
/** @deprecated 使用 ABR_HOME_SUBMENU */
export const ABR_DEVELOP_V2_PATH_SEGMENT = ABR_HOME_SUBMENU;

const WORKSPACE_ROUTE_PREFIX_KEY = 'workspace-route-prefix';

/** 记录用户从哪个版本的工作台进入（'' = 新版，'/v1' = 经典版） */
export function setWorkspaceRoutePrefix(prefix: string): void {
  try {
    localStorage.setItem(WORKSPACE_ROUTE_PREFIX_KEY, prefix);
  } catch {
    // ignore
  }
}

function getWorkspaceRoutePrefix(): string {
  try {
    return localStorage.getItem(WORKSPACE_ROUTE_PREFIX_KEY) ?? '';
  } catch {
    return '';
  }
}

/** 应用入口默认落地页：ABR 开启时进入新版首页，否则进入 v1 经典版。 */
export function getAbrDefaultEntryPath(): string {
  return isAbrRebrandEnabled() ? ABR_DEFAULT_ENTRY_PATH : COZE_V1_ENTRY_PATH;
}

/** ABR 新版首页完整路径 */
export function getAbrDevelopHomePath(spaceId: string): string {
  return `${ABR_DEFAULT_ENTRY_PATH}/${spaceId}/${ABR_HOME_SUBMENU}`;
}

/** 旧版 v1 开发页完整路径 */
export function getCozeV1DevelopHomePath(spaceId: string): string {
  return `${COZE_V1_ENTRY_PATH}/${spaceId}/${ABR_HOME_SUBMENU}`;
}

/** 工作空间子模块路径（develop / library），兼容 v1 前缀 */
export function getSpaceSubmodulePath(
  spaceId: string,
  submodule: 'develop' | 'library',
): string {
  const routePrefix = getWorkspaceRoutePrefix();
  if (routePrefix === COZE_V1_ROUTE_PREFIX) {
    return `${COZE_V1_ENTRY_PATH}/${spaceId}/${submodule}`;
  }
  return `${ABR_DEFAULT_ENTRY_PATH}/${spaceId}/${submodule}`;
}

/** @deprecated 使用 getSpaceSubmodulePath */
export function getDetailBackPath(
  spaceId: string,
  cozeFallback: 'develop' | 'library' = 'develop',
): string {
  return getSpaceSubmodulePath(spaceId, cozeFallback);
}

export const ABR_PRODUCT_NAME_ZH = '爱波瑞 Agent';
export const ABR_PRODUCT_NAME_EN = 'Abrain Agent';
export const ABR_STUDIO_TITLE_ZH = '爱波瑞 Agent';
export const ABR_STUDIO_TITLE_EN = 'Abrain Agent Studio';

export function getAbrProductName(isOversea = false): string {
  return isOversea ? ABR_PRODUCT_NAME_EN : ABR_PRODUCT_NAME_ZH;
}

export function getAbrStudioTitle(isOversea = false): string {
  return isOversea ? ABR_STUDIO_TITLE_EN : ABR_STUDIO_TITLE_ZH;
}
